import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import { ClipboardCheck, ClipboardCopy } from 'lucide-react';
import StructureSwatch from '~/components/Structures/StructureSwatch';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import {
    FORMULA_OPERATOR_TEXT,
    FormulaToken,
    formulaText,
} from '~/engine/util/algorithms/sdf/boolean-operations/booleanFormula';
import { useCopyToClipboard } from '~/hooks/useCopyToClipboard';
import { dicomColorToRgbString } from '~/utils/color';

/** Widest a structure chip grows, in px, before its name is ellipsized. */
const CHIP_MAX_WIDTH = 140;

/** The structure the operation writes. */
export interface FormulaResult {
    name: string;
    /** CSS colour of the structure's swatch. */
    color: string;
    /** The result replaces an existing structure's contours. */
    overwrites: boolean;
}

interface FormulaPreviewProps {
    tokens: readonly FormulaToken<RoiReadModel>[];
    /** Null while the operation has nowhere to write. */
    result: FormulaResult | null;
    'aria-label': string;
}

/**
 * The operation as a formula, bracketed in the order it runs, then the
 * structure it writes: primary for a new structure, warning for one it
 * overwrites. Copies the formula as text. Renders nothing while the chain
 * states no operation yet.
 */
export default function FormulaPreview({
    tokens,
    result,
    'aria-label': ariaLabel,
}: FormulaPreviewProps) {
    const { copy, status, label: copyLabel, reset } = useCopyToClipboard();
    if (tokens.length === 0) {
        return null;
    }
    return (
        <Box
            role="group"
            aria-label={ariaLabel}
            display="flex"
            alignItems="flex-start"
            gap={0.5}
            p={1}
            bgcolor="background.sunken"
            border={1}
            borderColor="divider"
            borderRadius={1}
        >
            <Box
                display="flex"
                flexWrap="wrap"
                alignItems="center"
                gap={0.5}
                flex={1}
                minWidth={0}
            >
                {tokens.map((token, index) => {
                    switch (token.kind) {
                        case 'operand':
                            return (
                                <Chip
                                    key={index}
                                    variant="tag"
                                    icon={
                                        <StructureSwatch
                                            color={dicomColorToRgbString(
                                                token.operand.color
                                            )}
                                        />
                                    }
                                    label={token.operand.name}
                                    title={token.operand.name}
                                    sx={{ maxWidth: CHIP_MAX_WIDTH }}
                                />
                            );
                        case 'operator':
                            return (
                                <Typography key={index} variant="monoMeta">
                                    {FORMULA_OPERATOR_TEXT[token.operation]}
                                </Typography>
                            );
                        case 'open':
                            return (
                                <Typography key={index} variant="monoMeta">
                                    (
                                </Typography>
                            );
                        case 'close':
                            return (
                                <Typography key={index} variant="monoMeta">
                                    )
                                </Typography>
                            );
                    }
                })}
                {result && (
                    <>
                        <Typography variant="monoMeta">{'\u2192'}</Typography>
                        <Chip
                            variant="tag"
                            color={result.overwrites ? 'warning' : 'primary'}
                            icon={<StructureSwatch color={result.color} />}
                            label={result.name}
                            title={result.name}
                            sx={{ maxWidth: CHIP_MAX_WIDTH }}
                        />
                    </>
                )}
            </Box>
            <Tooltip title={copyLabel}>
                <IconButton
                    size="small"
                    aria-label={copyLabel}
                    color={status === 'failed' ? 'error' : 'primary'}
                    onClick={() =>
                        copy(formulaText(tokens, (operand) => operand.name))
                    }
                    onMouseLeave={reset}
                    sx={{ m: -0.5 }}
                >
                    {status === 'copied' ? (
                        <ClipboardCheck size={12} />
                    ) : (
                        <ClipboardCopy size={12} />
                    )}
                </IconButton>
            </Tooltip>
        </Box>
    );
}
